import { format } from "date-fns";
import { ClientRepository } from "../interfaces";
import { ClientDetailRecord, ClientRecord, InvoiceRecord, ServiceRenderedRecord } from "../types";
import { InMemoryStore, nextId } from "./store";

export class InMemoryClientRepository implements ClientRepository {
  constructor(private store: InMemoryStore) {}

  async list(): Promise<ClientRecord[]> {
    return [...this.store.clients].sort((a, b) => {
      const aValue = a.reference ?? a.surname;
      const bValue = b.reference ?? b.surname;
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    });
  }

  async get(id: string): Promise<ClientDetailRecord> {
    const client = this.store.clients.find((c) => c.id === id);
    if (!client) throw new Error(`Client not found: ${id}`);
    return {
      ...client,
      invoices: this.store.invoices.filter((i) => i.clientID === id),
      servicesUsed: this.store.servicesRendered.filter((s) => s.clientId === id),
      pets: this.store.pets.filter((p) => p.clientID === id),
    };
  }

  async save(client: Partial<ClientRecord> & { id?: string }): Promise<ClientRecord> {
    if (client.id) {
      const idx = this.store.clients.findIndex((c) => c.id === client.id);
      if (idx < 0) throw new Error("Unable to find client to update");
      this.store.clients[idx] = { ...this.store.clients[idx], ...client };
      return this.store.clients[idx];
    }
    const created: ClientRecord = {
      name: client.name ?? "",
      surname: client.surname ?? "",
      ...client,
      id: nextId("client"),
    };
    this.store.clients.push(created);
    return created;
  }

  async delete(client: ClientRecord): Promise<void> {
    this.store.clients = this.store.clients.filter((c) => c.id !== client.id);
  }

  async getServicesRendered(clientId: string): Promise<ServiceRenderedRecord[]> {
    return this.store.servicesRendered.filter((s) => s.clientId === clientId);
  }

  // Mirrors DatastoreClientRepository.loadForInvoicing: aggregates un-invoiced
  // services rendered in the date range into one pending invoice per client,
  // then appends invoices that already exist in the range.
  async loadForInvoicing(startDate: Date, endDate: Date): Promise<InvoiceRecord[]> {
    const start = format(startDate, "yyyy-MM-dd");
    const end = format(endDate, "yyyy-MM-dd");

    const existingInvoices = this.store.invoices.filter(
      (i) => (i.startDate ?? "") >= start && (i.endDate ?? "") <= end
    );

    const inRange = this.store.servicesRendered.filter((s) => s.date >= start && s.date <= end && !s.invoiceID);

    const pendingByClient = new Map<string, InvoiceRecord>();
    for (const sr of inRange) {
      const service = this.store.services.find((s) => s.id === sr.serviceId);
      const cost = (sr.serviceCost ?? service?.cost ?? 0) * sr.quantity + (sr.mileageCost ?? 0);
      const existing = pendingByClient.get(sr.clientId);
      if (!existing) {
        pendingByClient.set(sr.clientId, {
          id: sr.clientId,
          clientID: sr.clientId,
          invoiceName: "",
          subtotal: cost,
          startDate: sr.date,
          endDate: sr.date,
          servicesRendered: [sr],
        });
      } else {
        existing.subtotal = (existing.subtotal ?? 0) + cost;
        existing.startDate = !existing.startDate || sr.date < existing.startDate ? sr.date : existing.startDate;
        existing.endDate = !existing.endDate || sr.date > existing.endDate ? sr.date : existing.endDate;
        existing.servicesRendered.push(sr);
      }
    }

    return [...Array.from(pendingByClient.values()), ...existingInvoices.map((i) => ({ ...i }))];
  }
}
